/**
 * Selo de autoridade do campo (Task 11 / D-11.2) · redesign Entrega 3.
 *
 * É o que torna visível, na tela, quem é dono do dado: campo de autoridade
 * Bling aparece como **somente-leitura** ("Fonte: Bling"), campo da Plataforma
 * como **editável**, e disponibilidade como **derivada**. A decisão não é da
 * tela: vem de `packages/catalog`, espelho das migrations 0019/0030.
 *
 * `data-authority` e `data-editable` existem no markup de propósito: o aceite
 * "campo do Bling é somente-leitura e campo da Plataforma é editável" é
 * verificável no render, sem navegador.
 *
 * Redesign (apresentação apenas): selo com ÍCONE + TEXTO (cor nunca é o único
 * indicador — régua §2). A autoridade Bling (somente-leitura) usa tom neutro
 * com cadeado; Plataforma (editável) usa o accent da marca; derivado usa tom de
 * informação. `title`/texto do selo PRESERVADOS (vêm de `authorityBadgeLabel`).
 */
import { createElement as h, type ReactElement } from "react";
import { GitMerge, Lock, type LucideIcon, Pencil } from "lucide-react";
import { authorityBadgeLabel, authorityOf, isPlatformEditable, type FieldAuthority } from "../../../../packages/catalog/src/index.js";

export interface AuthorityBadgeProps {
  fieldKey: string;
}

const TONE: Record<FieldAuthority, string> = {
  bling: "border-border-strong text-body",
  platform: "border-brand text-brand-text",
  consolidated: "border-border-strong text-body"
};

const ICON_BY_AUTHORITY: Record<FieldAuthority, LucideIcon> = {
  bling: Lock,
  platform: Pencil,
  consolidated: GitMerge
};

function iconFor(authority: FieldAuthority): ReactElement {
  const Icon = ICON_BY_AUTHORITY[authority];
  return h(Icon, { className: "h-3 w-3 shrink-0", "aria-hidden": true, focusable: false });
}

export function AuthorityBadge({ fieldKey }: AuthorityBadgeProps): ReactElement {
  const authority = authorityOf(fieldKey);
  const editable = isPlatformEditable(fieldKey);
  const label = authorityBadgeLabel(authority);
  return h(
    "span",
    {
      className: `authority-badge authority-badge--${authority} inline-flex items-center gap-1 whitespace-nowrap rounded-pill border bg-surface px-2 py-0.5 text-[11px] font-bold ${TONE[authority]}`,
      "data-field-key": fieldKey,
      "data-authority": authority,
      "data-editable": editable ? "true" : "false",
      title: label
    },
    iconFor(authority),
    h("span", null, label)
  );
}
